import React, { createContext, useContext, useMemo, useState } from 'react';
import {
  InfiniteProgramsState,
  useInfinitePrograms,
} from './useInfinitePrograms';

/**
 * All list state (data + filters + local mutators) lives here so every
 * consumer — the page shell, filter bar, cards, and modals — reads the same
 * copy. Mutators like `removeProgramLocal` let modals update the list without
 * a full refetch.
 */
export interface ProgramsContextValue extends InfiniteProgramsState {
  filters: {
    search: string;
    status: string | undefined;
    stage: string | undefined;
  };
  setSearch: (v: string) => void;
  setStatus: (v: string | undefined) => void;
  setStage: (v: string | undefined) => void;
}

const ProgramsContext = createContext<ProgramsContextValue | null>(null);

export const ProgramsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [stage, setStage] = useState<string | undefined>(undefined);

  const filters = useMemo(() => ({ search, status, stage }), [
    search,
    status,
    stage,
  ]);

  const listState = useInfinitePrograms(filters);

  const value = useMemo<ProgramsContextValue>(
    () => ({
      ...listState,
      filters,
      setSearch,
      setStatus,
      setStage,
    }),
    [listState, filters],
  );

  return (
    <ProgramsContext.Provider value={value}>
      {children}
    </ProgramsContext.Provider>
  );
};

export const usePrograms = (): ProgramsContextValue => {
  const ctx = useContext(ProgramsContext);
  if (!ctx) {
    throw new Error('usePrograms must be used inside a ProgramsProvider');
  }
  return ctx;
};

/**
 * Non-throwing variant for components that may render both inside and outside
 * the ProgramsProvider (e.g. ProgramQuestionsModal, which is opened from
 * Programs & Reports and from ReportBuilder). Callers must null-check.
 */
export const useOptionalPrograms = (): ProgramsContextValue | null =>
  useContext(ProgramsContext);
